import api from '../../../lib/api';

export const LLM_SETTINGS_LOAD = 'LLM_SETTINGS_LOAD';
export const LLM_SETTINGS_LIST_LOAD = 'LLM_SETTINGS_LIST_LOAD';
export const LLM_SETTINGS_UPDATE = 'LLM_SETTINGS_UPDATE';
export const LLM_PROVIDERS_LOAD = 'LLM_PROVIDERS_LOAD';

const LLM_SETTINGS_CHANGE = 'LLM_SETTINGS_CHANGE';
const LLM_SETTINGS_LIST_CHANGE = 'LLM_SETTINGS_LIST_CHANGE';
const LLM_SETTINGS_LIST_LOADING_CHANGE = 'LLM_SETTINGS_LIST_LOADING_CHANGE';
const LLM_SETTINGS_LOADING_CHANGE = 'LLM_SETTINGS_LOADING_CHANGE';
const LLM_SETTINGS_SAVING_CHANGE = 'LLM_SETTINGS_SAVING_CHANGE';
const LLM_SETTINGS_ERRORS_CHANGE = 'LLM_SETTINGS_ERRORS_CHANGE';
const LLM_PROVIDERS_CHANGE = 'LLM_PROVIDERS_CHANGE';
const LLM_PROVIDERS_LOADING_CHANGE = 'LLM_PROVIDERS_LOADING_CHANGE';
const LLM_PROVIDERS_ERROR_CHANGE = 'LLM_PROVIDERS_ERROR_CHANGE';

const auth = JSON.parse(localStorage.getItem('users.auth_user', '{"lang":"en"}')) || {lang:'en'};
const getTranslate = require(`./../../../langs/llm_settings/${auth.lang}.json`) || {};

const state = {
  // Текущие настройки (для обратной совместимости)
  llm_settings: null,
  llm_settings_loading: false,
  llm_settings_saving: false,
  llm_settings_errors: {},

  // Список настроек по всем провайдерам
  llm_settings_list: [],
  llm_settings_list_loading: false,

  // Реестр доступных провайдеров и их моделей
  llm_providers: [],
  llm_providers_loading: false,
  llm_providers_error: null,
  // Promise текущей загрузки для предотвращения дублей
  llm_providers_loading_promise: null,

  // Шаблон пустых настроек
  llm_settings_empty: {
    organization_id: null,
    provider_type: '',
    default_model: '',
    api_key: '',
  },
};

/**
 * Получает ID организации авторизованного пользователя
 * @returns {number} ID организации или 0, если не найден
 */
const getCurrentOrganizationId = (rootState) => {
  const auth_user = rootState?.users?.auth_user;
  let organization_id = 0;

  if (auth_user && auth_user?.company?.[0]?.id) {
    organization_id = auth_user.company[0].id;
  }

  return organization_id;
};

/**
 * Проверяет, есть ли настройка для выбранного провайдера
 * @param {Array} settingsList
 * @param {Object} model
 * @returns {boolean}
 */
const hasProviderSettings = (settingsList = [], model = {}) => {
  if (!model.provider_type) {
    return false;
  }

  return settingsList.some((item) => (
    item.provider_type === model.provider_type
    && item.id !== model.id
  ));
};

/**
 * Вспомогательная функция для UPSERT записи в список настроек
 * @param {Array} list
 * @param {Object} item
 * @returns {Array}
 */
const upsertSetting = (list = [], item = {}) => {
  const nextList = [...list];
  const index = nextList.findIndex((entry) => {
    if (entry.id && item.id) {
      return entry.id === item.id;
    }
    return entry.provider_type === item.provider_type
      && entry.default_model === item.default_model;
  });
  if (index >= 0) {
    nextList.splice(index, 1, item);
  } else {
    nextList.push(item);
  }
  return nextList;
};

const actions = {
  /**
   * Загружает реестр доступных провайдеров из llm-executor
   */
  [LLM_PROVIDERS_LOAD]({ commit, state }, param = {}) {
    if (state.llm_providers_loading && state.llm_providers_loading_promise) {
      return state.llm_providers_loading_promise;
    }

    if (state.llm_providers.length && !param.force) {
      return Promise.resolve(state.llm_providers);
    }

    const options = param?.options || {};
    commit(LLM_PROVIDERS_ERROR_CHANGE, null);

    // Используем обертку для перехвата ошибок до api.load
    // api.load не сбрасывает loading при ошибке и возвращает false вместо rejected Promise
    const loadingPromise = new Promise((resolve, reject) => {
      api.load(
        'llm.providers.list',
        '',
        state,
        commit,
        LLM_PROVIDERS_LOADING_CHANGE,
        LLM_PROVIDERS_CHANGE,
        null,
        options,
      ).then((result) => {

        if (result === false) {
          commit(LLM_PROVIDERS_LOADING_CHANGE, false);
          const error = {
            message: getTranslate.failed_to_load_llm_providers,
            data: { message: getTranslate.failed_to_load_llm_providers }
          };
          commit(LLM_PROVIDERS_ERROR_CHANGE, error);
          reject(error);
        } else if (result === true) {
          commit(LLM_PROVIDERS_ERROR_CHANGE, null);
          resolve(result);
        } else {
          commit(LLM_PROVIDERS_LOADING_CHANGE, false);
          const error = {
            message: getTranslate.unexpected_response_from_server,
            data: { message: getTranslate.unexpected_response_from_server }
          };
          commit(LLM_PROVIDERS_ERROR_CHANGE, error);
          reject(error);
        }
      }).catch((error) => {
        commit(LLM_PROVIDERS_LOADING_CHANGE, false);
        const errorObj = error && typeof error === 'object'
          ? error
          : { message: String(error), data: { message: String(error) } };
        commit(LLM_PROVIDERS_ERROR_CHANGE, errorObj);
        reject(errorObj);
      });
    }).finally(() => {
      commit('LLM_PROVIDERS_LOADING_PROMISE_CHANGE', null);
    });

    commit('LLM_PROVIDERS_LOADING_PROMISE_CHANGE', loadingPromise);

    return loadingPromise;
  },

  /**
   * Загружает список настроек LLM для текущей организации
   */
  [LLM_SETTINGS_LIST_LOAD]({ commit, state }, param = {}) {
    const options = param?.options || {};

    return api.load(
      'llm.settings.list',
      '',
      state,
      commit,
      LLM_SETTINGS_LIST_LOADING_CHANGE,
      LLM_SETTINGS_LIST_CHANGE,
      LLM_SETTINGS_ERRORS_CHANGE,
      options,
    );
  },

  /**
   * Загружает настройки конкретного провайдера
   */
  [LLM_SETTINGS_LOAD]({ commit, state }, param = {}) {
    const options = param?.options || {};
    const provider_type = param?.provider_type || 'gemini';

    return api.load(
      'llm.settings.item',
      provider_type,
      state,
      commit,
      LLM_SETTINGS_LOADING_CHANGE,
      LLM_SETTINGS_CHANGE,
      LLM_SETTINGS_ERRORS_CHANGE,
      options,
    );
  },

  /**
   * Обновляет/создает настройки LLM для текущей организации
   */
  [LLM_SETTINGS_UPDATE]({
    commit,
    state,
    dispatch,
    rootState,
  }, param) {
    const model = { ...(param.model || {}) };
    const options = param.options || {};

    // Валидация уникальности провайдера
    if (hasProviderSettings(state.llm_settings_list, model)) {
      const duplicateError = {
        provider_settings_exists: ['provider_settings_exists'],
      };
      commit(LLM_SETTINGS_ERRORS_CHANGE, duplicateError);
      return Promise.reject(duplicateError);
    }

    // Получаем ID организации из auth_user и добавляем в модель
    const organization_id = getCurrentOrganizationId(rootState);
    if (organization_id) {
      model.organization_id = organization_id;
    }

    return api.create(
      'llm.settings.save',
      model.provider_type || '',
      model,
      state,
      commit,
      LLM_SETTINGS_SAVING_CHANGE,
      LLM_SETTINGS_CHANGE,
      LLM_SETTINGS_ERRORS_CHANGE,
      options,
    ).then((result) => {
      if (result) {
        dispatch(LLM_SETTINGS_LIST_LOAD);
      }
      return result;
    });
  },
};

const mutations = {
  [LLM_SETTINGS_CHANGE](state, data) {
    state.llm_settings = data || null;
    if (data) {
      state.llm_settings_list = upsertSetting(state.llm_settings_list, data);
    }
  },

  [LLM_SETTINGS_LIST_CHANGE](state, data) {
    if (data === null) {
      return;
    }
    if (Array.isArray(data)) {
      state.llm_settings_list = data;
    } else if (data) {
      state.llm_settings_list = upsertSetting(state.llm_settings_list, data);
    } else {
      state.llm_settings_list = [];
    }

    state.llm_settings = state.llm_settings_list.length
      ? state.llm_settings_list[0]
      : null;
  },

  [LLM_SETTINGS_LIST_LOADING_CHANGE](state, data) {
    state.llm_settings_list_loading = data;
  },

  [LLM_SETTINGS_LOADING_CHANGE](state, data) {
    state.llm_settings_loading = data;
  },

  [LLM_SETTINGS_SAVING_CHANGE](state, data) {
    state.llm_settings_saving = data;
  },

  [LLM_SETTINGS_ERRORS_CHANGE](state, data) {
    state.llm_settings_errors = data || {};
    state.llm_settings_loading = false;
    state.llm_settings_list_loading = false;
    state.llm_settings_saving = false;
  },

  [LLM_PROVIDERS_CHANGE](state, data) {
    state.llm_providers = data || [];
  },

  [LLM_PROVIDERS_LOADING_CHANGE](state, data) {
    state.llm_providers_loading = data;
  },

  [LLM_PROVIDERS_ERROR_CHANGE](state, error) {
    state.llm_providers_error = error;
    state.llm_providers_loading = false;
  },

  LLM_PROVIDERS_LOADING_PROMISE_CHANGE(state, promise) {
    state.llm_providers_loading_promise = promise;
  },
};

const getters = {
  isLoading: (state) => state.llm_settings_loading || state.llm_settings_list_loading,
  isSaving: (state) => state.llm_settings_saving,
  providers: (state) => state.llm_providers,
  settingsList: (state) => state.llm_settings_list,
  providersLoading: (state) => state.llm_providers_loading,
  providersError: (state) => state.llm_providers_error,
  hasProvidersError: (state) => state.llm_providers_error !== null,
  providersMap: (state) => state.llm_providers.reduce((acc, provider) => {
    if (provider?.type) {
      acc[provider.type] = provider;
    }
    return acc;
  }, {}),
  settingsMap: (state) => state.llm_settings_list.reduce((acc, setting) => {
    if (setting?.provider_type) {
      acc[setting.provider_type] = setting;
    }
    return acc;
  }, {}),
  hasSettings: (state) => Array.isArray(state.llm_settings_list) && state.llm_settings_list.length > 0,
  findSettingByProvider: (state) => (providerType) => (
    state.llm_settings_list.find((item) => item.provider_type === providerType) || null
  ),
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
